import { useState } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/auth/AuthProvider'
import type { AppRole } from '@/api/types/auth'
import { getApiErrorMessage } from '@/api/client'
import { useToast } from '@/components/ToastProvider'

const ROLE_OPTIONS: Array<{ value: AppRole; label: string }> = [
  { value: 'STATION_MANAGER', label: '负责人' },
  { value: 'PROCUREMENT', label: '采购' },
  { value: 'SALES', label: '销售' },
  { value: 'ENGINEER', label: '工程师' },
  { value: 'ADMIN', label: '管理员' },
]

export default function LoginPage() {
  const { login } = useAuth()
  const toast = useToast()
  const [userId, setUserId] = useState('pc.manager')
  const [userName, setUserName] = useState('PC Web 用户')
  const [role, setRole] = useState<AppRole>('STATION_MANAGER')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    try {
      await login({ userId: userId.trim(), userName: userName.trim(), role })
      toast.success('登录成功')
    } catch (error) {
      toast.error(getApiErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-blue-50 px-6">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl lg:grid-cols-[1.15fr_0.85fr]">
        <div className="hidden bg-slate-900 px-10 py-12 text-white lg:flex lg:flex-col lg:justify-between">
          <div>
            <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-semibold">医疗设备维修工作台</h1>
            <p className="mt-4 max-w-md text-sm leading-7 text-slate-300">
              PC-Web 现在统一走 Bearer Token。登录后，采购、库存、设备、工单和销售页面会按当前角色的真实权限访问后端。
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-slate-200">
            当前环境提供的是开发登录入口，用来模拟不同岗位在 PC-Web 的实际操作闭环。
          </div>
        </div>

        <div className="px-6 py-8 sm:px-10 sm:py-12">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900">登录</h2>
            <p className="mt-2 text-sm text-gray-500">请输入当前操作人的账号信息。</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">用户 ID</label>
              <input
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="例如 pc.manager"
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">用户名称</label>
              <input
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="例如 南京站长"
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">角色</label>
              <select
                value={role}
                onChange={(event) => setRole(event.target.value as AppRole)}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isSubmitting ? '登录中...' : '进入系统'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
